/**
 * Agent listing breakdown component.
 * Shows list of agents with their avatars and listing counts.
 */

import { useTranslation } from 'react-i18next';

interface AgentStats {
  agentId: string;
  displayName: string;
  avatarUrl?: string;
  count: number;
}

interface AgentBreakdownProps {
  agents: AgentStats[];
}

/**
 * Get initials from a display name (first letter of first two words).
 */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}

export function AgentBreakdown({ agents }: AgentBreakdownProps) {
  const { t } = useTranslation('dashboard');

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold text-[var(--pv-color-primary)]">
        {t('company.agentBreakdown.title')}
      </h2>

      {agents.length === 0 ? (
        <div className="rounded-xl bg-white p-6 text-center shadow-sm">
          <p className="text-gray-500">{t('company.empty.noAgents')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map((agent) => (
            <div
              key={agent.agentId}
              className="flex items-center rounded-xl bg-white p-4 shadow-sm"
            >
              {/* Avatar */}
              {agent.avatarUrl ? (
                <img
                  src={agent.avatarUrl}
                  alt={agent.displayName}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#004D40]/10">
                  <span className="text-sm font-medium text-[#004D40]">
                    {getInitials(agent.displayName)}
                  </span>
                </div>
              )}

              {/* Display name */}
              <span className="ml-3 flex-1 truncate font-medium text-gray-900">
                {agent.displayName}
              </span>

              {/* Count badge */}
              <span className="rounded-lg bg-[#004D40]/10 px-3 py-1 text-sm font-medium text-[#004D40]">
                {agent.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
